import LegalPage from "@/components/site/LegalPage";

const sections = [
  {
    n: "1.0",
    title: "Information We Collect",
    body: (
      <>
        <p className="mb-4">We may collect the following information:</p>
        <h3 className="text-lg font-extrabold mt-4 mb-2" style={{ color: "var(--on-surface)" }}>
          1.1 Personal Information
        </h3>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Full name</li>
          <li>Email address</li>
          <li>Mobile phone number (if provided)</li>
          <li>Profile photo (if provided)</li>
          <li>Account credentials</li>
          <li>Subscription information</li>
        </ul>
        <h3 className="text-lg font-extrabold mt-4 mb-2" style={{ color: "var(--on-surface)" }}>
          1.2 Usage Information
        </h3>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Campsite check-in history</li>
          <li>Digital passport stamps</li>
          <li>Achievement and activity records</li>
          <li>Device information</li>
          <li>Application usage statistics</li>
          <li>Crash reports and diagnostics</li>
        </ul>
        <h3 className="text-lg font-extrabold mt-4 mb-2" style={{ color: "var(--on-surface)" }}>
          1.3 Location Information
        </h3>
        <p className="mb-3">
          With your permission, MyAdventure-Passport may collect your device's
          location to:
        </p>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <li>Verify campsite check-ins.</li>
          <li>Display nearby campsites.</li>
          <li>Improve location-based services.</li>
        </ul>
        <p>Location data is collected only when necessary and with your consent.</p>
      </>
    ),
  },
  {
    n: "2.0",
    title: "How We Use Your Information",
    body: (
      <>
        <p className="mb-3">We use your information to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Create and manage your account.</li>
          <li>Verify campsite check-ins.</li>
          <li>Issue digital passport stamps.</li>
          <li>Provide premium subscription features.</li>
          <li>Improve application performance and user experience.</li>
          <li>Respond to customer support enquiries.</li>
          <li>Detect fraudulent or unauthorized activities.</li>
          <li>Comply with legal obligations.</li>
        </ul>
      </>
    ),
  },
  {
    n: "3.0",
    title: "Information Sharing",
    body: (
      <>
        <p className="mb-3">
          We do not sell, rent, or trade your personal information.
        </p>
        <p className="mb-3">We may share your information only with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Participating campsite operators for verification of check-ins where
            necessary.
          </li>
          <li>Payment service providers for subscription processing.</li>
          <li>
            Cloud hosting and infrastructure providers supporting the
            application.
          </li>
          <li>
            Government authorities or law enforcement when required by
            applicable laws.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "4.0",
    title: "Data Security",
    body: (
      <>
        <p className="mb-3">
          We implement appropriate technical and organizational security
          measures to protect your information from unauthorized access,
          disclosure, alteration, or destruction.
        </p>
        <p>
          While we strive to protect your information, no method of electronic
          transmission or storage is completely secure.
        </p>
      </>
    ),
  },
  {
    n: "5.0",
    title: "Data Retention",
    body: (
      <>
        <p className="mb-3">
          We retain your personal information only for as long as necessary to:
        </p>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <li>Maintain your account.</li>
          <li>Provide application services.</li>
          <li>Fulfil legal, regulatory, and operational requirements.</li>
        </ul>
        <p>
          When your information is no longer required, it will be securely
          deleted or anonymized where appropriate.
        </p>
      </>
    ),
  },
  {
    n: "6.0",
    title: "Your Rights",
    body: (
      <>
        <p className="mb-3">
          Depending on applicable laws, you may have the right to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access your personal information.</li>
          <li>Update or correct inaccurate information.</li>
          <li>Request deletion of your account and personal data.</li>
          <li>
            Withdraw consent for optional data collection, such as location
            services.
          </li>
          <li>Contact us regarding your privacy concerns.</li>
        </ul>
      </>
    ),
  },
  {
    n: "7.0",
    title: "Third-Party Services",
    body: (
      <>
        <p className="mb-3">
          MyAdventure-Passport may use trusted third-party services for:
        </p>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <li>Subscription and payment processing.</li>
          <li>Cloud hosting.</li>
          <li>Analytics and crash reporting.</li>
          <li>Push notifications.</li>
        </ul>
        <p>
          These providers process information in accordance with their own
          privacy policies and applicable laws.
        </p>
      </>
    ),
  },
  {
    n: "8.0",
    title: "Children's Privacy",
    body: (
      <p>
        MyAdventure-Passport application is not intended for children under the
        age required by applicable laws without parental or guardian consent.
        We do not knowingly collect personal information from children without
        appropriate authorization.
      </p>
    ),
  },
  {
    n: "9.0",
    title: "Changes to This Privacy Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. Any changes will
        become effective upon publication within the application or on our
        official website. Continued use of the application after such changes
        constitutes acceptance of the updated Privacy Policy.
      </p>
    ),
  },
  {
    n: "10.0",
    title: "Contact Us",
    body: (
      <>
        <p className="mb-3">
          If you have any questions regarding this Privacy Policy or your
          personal information, please contact us at:
        </p>
        <p className="font-bold mb-1">MyAdventure-Passport Support</p>
        <p className="mb-1">Email: support@myadventure-passport.com</p>
        <p>Website: www.myadventure-passport.com</p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Privacy Policy"
      title="Privacy Policy"
      effective="1st July 2026"
    >
      <p className="mb-8">
        MyAdventure-Passport ("we", "our", or "us") is committed to protecting
        your privacy and ensuring that your personal information is handled
        securely and responsibly. This Privacy Policy explains how we collect,
        use, disclose, and protect your information when you use the
        MyAdventure-Passport mobile application.
      </p>
      {sections.map((s) => (
        <div key={s.n} className="mb-8">
          <h2 className="text-xl md:text-2xl font-extrabold mb-3" style={{ color: "var(--on-surface)" }}>
            <span style={{ color: "var(--premium)" }}>{s.n}</span> &nbsp; {s.title}
          </h2>
          <div style={{ color: "var(--on-surface)" }}>{s.body}</div>
        </div>
      ))}
    </LegalPage>
  );
}
